import { BsBell } from "react-icons/bs";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";

import { Spinner } from "@/components/ui/spinner/spinner";
import {
  fetchCurrentAdminNotifications,
  fetchUnseenNotificationCountForAdmin,
} from "@/config/queries/admin/admin-notification-queries";
import { Link } from "react-router-dom";

function getTimeSinceNotification(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const elapsedMilliseconds = now.getTime() - date.getTime();
  const elapsedSeconds = Math.floor(elapsedMilliseconds / 1000);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  const elapsedDays = Math.floor(elapsedHours / 24);

  if (elapsedDays > 0) {
    return `${elapsedDays} day${elapsedDays > 1 ? "s" : ""} ago`;
  } else if (elapsedHours > 0) {
    return `${elapsedHours} hour${elapsedHours > 1 ? "s" : ""} ago`;
  } else if (elapsedMinutes > 0) {
    return `${elapsedMinutes} minute${elapsedMinutes > 1 ? "s" : ""} ago`;
  } else {
    return `${elapsedSeconds} second${elapsedSeconds !== 1 ? "s" : ""} ago`;
  }
}

const linkMappings: {
  [key: string]: string;
} = {
  Allowance: "/admin/allowance",
  Announcement: "/admin/announcements",
  Salary: "/admin/salaries",
  "Leave Application": "/admin/leave-applications",
  Todo: "/admin/todos",
  Loan: "/admin/other-loans",
  "Loan Repayment Request": "/admin/other-loans",
  "Attendance Request": "/admin/employee-attendance-requests",
  "Performance Report": "/employee",
  DateSheet: "/admin/salary-timesheets",
};

// @ts-nocheck
export const AdminNotifications = () => {
  const adminKey = Cookies.get("key") as string;
  const [open, setOpen] = useState(false);

  const container = useRef(null);

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const { data: NotificaitonCountData } = useQuery({
    queryKey: ["admin-unseen-notifications"],
    queryFn: () =>
      fetchUnseenNotificationCountForAdmin({
        adminKey,
      }),
    refetchOnWindowFocus: true,
  });

  const { data, hasNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: ["admin-notifications"],
    queryFn: async ({ pageParam }) =>
      fetchCurrentAdminNotifications({
        page: pageParam,
        adminKey,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.remaining > 0 ? allPages?.length + 1 : undefined;
    },
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  // useEffect(() => {
  //   let allNotis;

  //   if (data) {
  //     for (const page of data.pages) {
  //       allNotis = [...page.notifications];
  //     }

  //     const findOut = allNotis?.find((n) => !n?.isSeen);

  //     findOut && setShouldPing(true);
  //   }
  // }, [data, isFetching]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="transition-all duration-300 hover:bg-ring/10 p-2 rounded-full">
        <div className="relative">
          {NotificaitonCountData && NotificaitonCountData?.notSeen > 0 && (
            <div className="bg-blue-500 absolute rounded-full text-[11px] text-white p-1 -top-3 -right-3 flex items-center justify-center">
              {NotificaitonCountData?.notSeen}
            </div>
          )}
          <BsBell className="w-5 h-5" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="mr-5 mt-0.5 p-0 bg-popover text-popover-foreground dark:bg-[#282828] max-w-96 w-[24rem] dark:border dark:border-zinc-700">
        <div className="">
          <div className="border-b dark:border-b-zinc-600 flex flex-row justify-between items-center p-3">
            <h3>Notifications</h3>

            <Link
              to="/admin/notifications"
              onClick={() => {
                setOpen(false);
              }}
            >
              <span className="text-xs">View All</span>
            </Link>
          </div>

          {data?.pages && data?.pages?.length > 0 ? (
            <div>
              {data?.pages?.[0]?.notifications?.length > 0 ? (
                <div
                  className="flex flex-col max-h-[600px]  [scrollbar-width:thin] relative"
                  ref={container}
                  style={{
                    overflowY: "scroll",
                  }}
                >
                  {data?.pages?.map((page, index) => {
                    const notifications = page?.notifications;

                    return (
                      <div key={index}>
                        {notifications?.map((noti, i) => {
                          const href = linkMappings[noti?.article];

                          if (!href) {
                            return (
                              <div
                                className="relative p-[16px_16px_16px_8px] flex transition-all duration-[250ms] hover:bg-[#f4f4f4] dark:hover:bg-[#3c3b3b]"
                                key={i}
                              >
                                <div className="flex-1 flex mr-[24px]">
                                  {!noti?.isSeen && (
                                    <div className="bg-blue-400 w-1 h-1 rounded-[2px] m-[20px_6px_0] shrink-0" />
                                  )}

                                  <div className="flex flex-col gap-1 ml-1">
                                    <h2 className="text-base font-medium">
                                      {noti?.article}
                                    </h2>
                                    <div
                                      className="text-sm font-normal"
                                      dangerouslySetInnerHTML={{
                                        __html: noti?.notificationTitle,
                                      }}
                                    ></div>

                                    <div className="text-[10px] text-popover-foreground">
                                      {getTimeSinceNotification(
                                        noti?.createdAt
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <Link
                              to={href}
                              className="relative p-[16px_16px_16px_8px] flex transition-all duration-[250ms] hover:bg-[#f4f4f4] dark:hover:bg-[#3c3b3b]"
                              key={i}
                              onClick={() => setOpen(false)}
                            >
                              <div className="flex-1 flex mr-[24px]">
                                {!noti?.isSeen && (
                                  <div className="bg-blue-400 w-1 h-1 rounded-[2px] m-[20px_6px_0] shrink-0" />
                                )}

                                <div className="flex flex-col gap-1 ml-1">
                                  <h2 className="text-base font-medium">
                                    {noti?.article}
                                  </h2>
                                  <div
                                    className="text-sm font-normal"
                                    dangerouslySetInnerHTML={{
                                      __html: noti?.notificationTitle,
                                    }}
                                  ></div>

                                  <div className="text-[10px] text-popover-foreground">
                                    {getTimeSinceNotification(noti?.createdAt)}
                                  </div>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })}

                  <div ref={ref} className="h-1 flex-1 p-1" />

                  {isFetching && <Spinner className="mb-2"></Spinner>}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <h1>Nothing here yet</h1>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 text-center">
              <h1>Nothing here yet</h1>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
